import { faArrowsRotate, faDollarCircle, faEllipsisV, faEnvelopeOpenDollar, faExternalLink, faEye, faFileExport, faTrash } from "@fortawesome/pro-solid-svg-icons"
import { faMoneyCheckDollar } from "@fortawesome/pro-duotone-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Button, Dropdown, Modal } from "antd"
import { useContext, useState } from "react"
import { canInvoiceBeMarkedNotPaidManually, InvoiceSummaryWithCustomerJobData, isInvoiceIndependentlyPayable, wasInvoiceSent } from "../../../domain/invoicing/invoice"
import { StatementSummary } from "../../../domain/invoicing/statement"
import { resolveSendFlow, SendFlows } from "./invoiceActionsModel"
import { useEmailInvoiceMutation, useInvoiceDetailsQuery, useMarkInvoiceNotPaidManuallyMutation, useSyncInvoiceToQuickBooksMutation } from "../../../domain/invoicing/invoicingQueries"
import { shouldShowSyncInvoiceButton } from "../../../domain/invoicing/quickBooksTrackingModel"
import { AuthenticationContext } from "../../../AuthenticationContext"
import InvoiceActionsContext from "../AllInvoices/InvoiceActionsContext"
import { ItemType } from "antd/es/menu/interface"

type Props = {
  invoiceSummary: InvoiceSummaryWithCustomerJobData
  parentStatement?: StatementSummary
  defaultOpen?: boolean
}

const InvoiceActions = ({ invoiceSummary, parentStatement, defaultOpen = false }: Props) => {
  const { currentUser } = useContext(AuthenticationContext)
  const { openModal } = useContext(InvoiceActionsContext)
  const isTenantConnectedToQuickBooks = currentUser?.isTenantConnectedToQuickBooks === true

  const [hasBeenOpened, setHasBeenOpened] = useState(defaultOpen)
  const [isOpen, setIsOpen] = useState(defaultOpen)

  const isSynkedUP = invoiceSummary.invoicingSystem === 'synkedUp'
  const isQuickBooks = invoiceSummary.invoicingSystem === 'quickBooks'
  const isExported = invoiceSummary.qboInvoiceId.length > 0
  const isInvoicePaid = invoiceSummary.balance < invoiceSummary.total

  const detailsQuery = useInvoiceDetailsQuery(hasBeenOpened ? invoiceSummary.invoiceId : '')

  const syncMutation = useSyncInvoiceToQuickBooksMutation()
  const notPaidMutation = useMarkInvoiceNotPaidManuallyMutation()
  const emailMutation = useEmailInvoiceMutation()

  const controlsDisabled = syncMutation.isLoading || notPaidMutation.isLoading || emailMutation.isLoading

  const showSync = shouldShowSyncInvoiceButton(isTenantConnectedToQuickBooks, invoiceSummary)
  const showMarkNotPaid = canInvoiceBeMarkedNotPaidManually(invoiceSummary)
  const showViewReceipt = invoiceSummary.balance === 0 && (detailsQuery.data?.paymentPageUrl?.length ?? 0) > 0
  const showSendItem = (isSynkedUP && invoiceSummary.balance > 0) || (isQuickBooks && isExported)
  const showPayInvoice = isSynkedUP && invoiceSummary.balance > 0 && isInvoiceIndependentlyPayable(invoiceSummary, parentStatement)
  const showOpenInQbo = isExported
  const showExportToQbo = isQuickBooks && !isExported

  const wasSent = wasInvoiceSent(invoiceSummary)

  const handleSendItemClick = () => {
    const flow = resolveSendFlow(invoiceSummary)
    switch (flow) {
      case SendFlows.Reminder: {
        openModal('emailReminder', invoiceSummary)
        return
      }
      case SendFlows.SynkedUPSend: {
        openModal('sendInvoice', invoiceSummary)
        return
      }
      case SendFlows.QuickBooksEmail: {
        const emailAddress = detailsQuery.data?.quickBooksInvoice?.emailAddress ?? ''
        const emailTo = emailAddress.length > 0 ? emailAddress : 'the customer'
        Modal.confirm({
          title: `Have QuickBooks email this invoice to ${emailTo}?`,
          okText: 'Send',
          onOk: () => emailMutation.mutate(invoiceSummary.invoiceId)
        })
        return
      }
    }
  }

  const items: ItemType[] = [
    {
      key: 'view-invoice',
      icon: <FontAwesomeIcon icon={faEye} />,
      label: 'View Invoice Details',
      onClick: () => openModal('viewInvoice', invoiceSummary)
    },
    showSendItem ? {
      key: 'send-item',
      icon: <FontAwesomeIcon icon={faEnvelopeOpenDollar} />,
      label: wasSent ? 'Send Reminder to Customer' : 'Send Invoice to Customer',
      disabled: controlsDisabled,
      onClick: handleSendItemClick
    } : null,
    showMarkNotPaid ? {
      key: 'mark-not-paid',
      icon: <FontAwesomeIcon icon={faDollarCircle} />,
      label: 'Mark Invoice Not Paid',
      disabled: controlsDisabled,
      onClick: () => {
        Modal.confirm({
          title: 'Are you sure you want to manually mark this invoice not paid?',
          okText: 'Yes',
          cancelText: 'No',
          onOk: () => notPaidMutation.mutate(invoiceSummary.invoiceId)
        })
      }
    } : null,
    showViewReceipt ? {
      key: 'view-receipt',
      icon: <FontAwesomeIcon icon={faEye} />,
      label: 'View Receipt',
      onClick: () => window.open(detailsQuery.data!.paymentPageUrl, '_blank')
    } : null,
    showPayInvoice ? {
      key: 'pay-invoice',
      icon: <FontAwesomeIcon icon={faMoneyCheckDollar} />,
      label: 'Pay Invoice',
      disabled: controlsDisabled,
      onClick: () => openModal('payInvoice', invoiceSummary)
    } : null,
    showExportToQbo ? {
      key: 'export-to-qb',
      icon: <FontAwesomeIcon icon={faFileExport} />,
      label: 'Export to QuickBooks',
      disabled: controlsDisabled || !detailsQuery.data,
      onClick: () => openModal('exportToQb', invoiceSummary)
    } : null,
    (showOpenInQbo || showSync) ? { type: 'divider' as const } : null,
    showOpenInQbo ? {
      key: 'open-in-qb',
      icon: <FontAwesomeIcon icon={faExternalLink} />,
      label: 'Open in QuickBooks Online',
      disabled: !detailsQuery.data || detailsQuery.data.qboInvoiceUrl.length === 0,
      onClick: () => window.open(detailsQuery.data!.qboInvoiceUrl, '_blank')
    } : null,
    showSync ? {
      key: 'sync-to-qb',
      icon: <FontAwesomeIcon icon={faArrowsRotate} />,
      label: 'Sync to QuickBooks',
      disabled: controlsDisabled,
      onClick: () => syncMutation.mutate(invoiceSummary.invoiceId)
    } : null,
    { type: 'divider' as const },
    {
      key: 'delete',
      icon: <FontAwesomeIcon icon={faTrash} />,
      label: 'Delete Invoice',
      danger: true,
      disabled: isInvoicePaid || controlsDisabled,
      onClick: () => openModal('delete', invoiceSummary)
    }
  ].filter(Boolean) as ItemType[]

  return <Dropdown
    trigger={['click']}
    open={isOpen}
    onOpenChange={(open) => {
      setIsOpen(open)
      if (open) {
        setHasBeenOpened(true)
      }
    }}
    menu={{ items }}
  >
    <Button
      type="text"
      icon={<FontAwesomeIcon icon={faEllipsisV} />}
      onClick={e => e.stopPropagation()}
    />
  </Dropdown>
}

export default InvoiceActions
